import { useState } from "react";
import { Save, X } from "lucide-react";
import { Button, Input, Label } from "@/design-system/components/ui";
import { isValidPhone, phoneFieldError } from "@/lib/phoneValidator";
import { leadsApi, type LeadSource } from "@/api/leads";
import { US_STATES } from "@/lib/usStates";
import { BrokeragePicker, type BrokeragePickerResult } from "@/components/brokerages/BrokeragePicker";
import {
  brokerageAffiliationsApi,
  AFFILIATION_TYPES,
  type AffiliationType,
} from "@/api/brokerages";
import { useToast } from "@/components/ui/Toast";

interface QuickAddLeadModalProps {
  onClose: () => void;
  onCreated: () => void;
}

/** v3.0 §5.3 Quick-Add — streamlined modal for high-volume data entry. */
export function QuickAddLeadModal({ onClose, onCreated }: QuickAddLeadModalProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [state, setState] = useState("");
  const [source, setSource] = useState<LeadSource>("ColdCall");
  const [brokerage, setBrokerage] = useState<BrokeragePickerResult | null>(null);
  const [affiliationType, setAffiliationType] = useState<AffiliationType>("Affiliated");
  const [isPrimary, setIsPrimary] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const toast = useToast();
  const phoneError = phone ? phoneFieldError(phone, { required: true }) : undefined;

  const save = async (saveAndNext: boolean) => {
    setError(null);
    if (!firstName || !lastName || !phone || !state) {
      setError("First name, last name, phone, and state are required.");
      return;
    }
    if (!isValidPhone(phone)) {
      setError("Enter a valid phone number.");
      return;
    }
    setSaving(true);
    try {
      const created = await leadsApi.create({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        state: state.toUpperCase().slice(0, 2),
        source,
        interestLevel: "Unknown",
      });
      // Brokerage Module v1.0 — affiliation is a secondary action; the lead
      // is already created, so a link failure surfaces a toast rather than
      // rolling back. The user can still link manually from the lead.
      if (brokerage) {
        try {
          await brokerageAffiliationsApi.link({
            brokerageId: brokerage.brokerageId,
            partnerId: created.id,
            partnerKind: "Lead",
            affiliationType,
            isPrimary,
            startDate: new Date().toISOString(),
          });
        } catch {
          toast.error(
            "Lead created, but the brokerage affiliation could not be linked. You can link it manually from the lead's Affiliations panel.",
          );
        }
      }
      onCreated();
      if (saveAndNext) {
        setFirstName("");
        setLastName("");
        setPhone("");
        setBrokerage(null);
        setAffiliationType("Affiliated");
        setIsPrimary(true);
        setMessage("Saved — enter next lead.");
        setTimeout(() => setMessage(null), 2000);
      } else {
        onClose();
      }
    } catch (e) {
      setError((e as Error).message ?? "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Quick add lead"
      className="fixed inset-0 z-40 bg-onyx/50 flex items-center justify-center p-space-4"
    >
      <div
        className="bg-white-pure rounded-lg shadow-lg max-w-lg w-full p-space-5 border-t-[2px] border-t-gold"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-space-3">
          <div>
            <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-1">
              Lead · quick add
            </div>
            <h2 className="font-display font-bold text-h3 text-onyx m-0">New lead</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-mid hover:text-onyx">
            <X size={18} strokeWidth={1.5} aria-hidden />
          </button>
        </div>

        <div className="grid gap-space-3">
          <div className="grid grid-cols-2 gap-space-3">
            <div>
              <Label>First name</Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} autoFocus />
            </div>
            <div>
              <Label>Last name</Label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-space-3">
            <div className="col-span-2">
              <Label>Phone</Label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                invalid={!!phoneError}
              />
              {phoneError && (
                <p className="text-label text-status-error mt-[6px]" role="alert">
                  {phoneError}
                </p>
              )}
            </div>
            <div>
              <Label>State</Label>
              <select
                className="h-10 w-full rounded-md border border-border-light bg-white-pure px-space-2 text-small"
                value={state}
                onChange={(e) => setState(e.target.value)}
              >
                <option value="">—</option>
                {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label>Source</Label>
            <select
              className="h-10 w-full rounded-md border border-border-light bg-white-pure px-space-2 text-small"
              value={source}
              onChange={(e) => setSource(e.target.value as LeadSource)}
            >
              {(["ColdCall","InboundInquiry","TradeShow","Referral","PurchasedList","IBBA","MaSource","LinkedIn","BizBuySell","Other"] as LeadSource[]).map(s =>
                <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <Label>Affiliated brokerage (optional)</Label>
            {brokerage ? (
              <div className="flex items-center justify-between rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
                <span className="text-small font-semibold text-onyx">{brokerage.brokerageName}</span>
                <button
                  type="button"
                  onClick={() => setBrokerage(null)}
                  aria-label="Remove brokerage"
                  className="text-gray-mid hover:text-onyx"
                >
                  <X size={16} strokeWidth={1.5} aria-hidden />
                </button>
              </div>
            ) : (
              <BrokeragePicker onSelect={setBrokerage} placeholder="Search brokerages…" />
            )}
          </div>

          {brokerage && (
            <div className="grid grid-cols-2 gap-space-3 items-end">
              <div>
                <Label>Affiliation type</Label>
                <select
                  className="h-10 w-full rounded-md border border-border-light bg-white-pure px-space-2 text-small"
                  value={affiliationType}
                  onChange={(e) => setAffiliationType(e.target.value as AffiliationType)}
                >
                  {AFFILIATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-space-2 h-10 text-small text-onyx">
                <input
                  type="checkbox"
                  checked={isPrimary}
                  onChange={(e) => setIsPrimary(e.target.checked)}
                />
                Primary affiliation
              </label>
            </div>
          )}

          {error && <div className="text-caption text-status-error">{error}</div>}
          {message && <div className="text-caption text-status-success">{message}</div>}
        </div>

        <div className="flex items-center justify-end gap-space-2 mt-space-4">
          <Button variant="text" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={saving}
            onClick={() => save(true)}
          >
            <Save size={14} strokeWidth={1.5} aria-hidden /> Save & next
          </Button>
          <Button size="sm" disabled={saving} onClick={() => save(false)}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
